/**
 * Force app update — the per-platform minimum app version.
 *
 * Below the floor for their platform, a member's app is replaced entirely by an
 * "Update Required" screen with one button. There is no way past it except
 * installing the update, and that includes the SIGN-IN screen — so this page is
 * the loudest control in the dashboard after the kill switch itself.
 *
 * COLOUR FOLLOWS THE HOUSE RULE IN pages/KillSwitch.tsx: red (#a4452f) belongs
 * to the kill switch alone, so that an admin scanning the dashboard in an
 * incident can find the one lever that takes the app offline. A force-update
 * floor is loud but not catastrophic and it is fully reversible within seconds,
 * so it gets GOLD — the same weight as the urgent notice.
 *
 * THE FAILURE THIS PAGE EXISTS TO PREVENT is an admin arming a floor at a
 * version that is not live in the store yet. Every member below it is then
 * locked out of an app they cannot fix, until someone comes back here. Three
 * things guard it, in increasing order of how much they trust the admin:
 *
 *   1. the switch is DISABLED until a valid floor has been saved, so
 *      "armed with no floor" cannot be typed into existence;
 *   2. a malformed version warns inline before the round trip, because the
 *      client silently ignores a floor it cannot parse (and the DB CHECK from
 *      20260921120000 would reject it anyway);
 *   3. arming routes through a confirmation that names the exact version and
 *      the exact store, and says out loud that it must already be live there.
 *
 * Turning a floor OFF never confirms — it only restores access, which is the
 * same asymmetry pages/KillSwitch.tsx applies to guest browsing.
 *
 * Talks to supabaseClient directly rather than through Refine, like the other
 * two app_config pages: the table's PK is a BOOLEAN (id = true), which does not
 * fit Refine's BaseKey. `describeWriteError` is imported from KillSwitch.tsx
 * rather than re-derived — supabase-js hands back a plain object, so String(e)
 * yields "[object Object]", and that reader is the house fix.
 */
import { useCallback, useEffect, useState } from 'react'
import { App, Button, Input, Switch } from 'antd'
import { supabaseClient } from '../lib/supabaseClient'
import { ListPage, SectionCard, type Stat } from '../theme/listkit'
import { brand } from '../theme/brand'
import { Download, ShieldCheck } from '../components/icons'
import { describeWriteError } from './KillSwitch'

const APP_CONFIG_RESOURCE = 'app_config'

/**
 * Every force-update column is OPTIONAL, because an admin build can be newer
 * than the database it is pointed at — the columns arrive in
 * 20260921120000_app_config_force_update.sql, and until then the read simply
 * returns a row without them rather than failing. Same reasoning as the
 * announcement columns in KillSwitch.tsx.
 */
interface ForceUpdateConfigRow {
  force_update_ios_active?: boolean | null
  force_update_ios_min_version?: string | null
  force_update_android_active?: boolean | null
  force_update_android_min_version?: string | null
  force_update_message?: string | null
  updated_at?: string | null
}

type PlatformKey = 'ios' | 'android'

/**
 * Three noun forms per platform rather than one with an 's' glued on: "iPhone
 * and iPad" does not pluralise to "iPhone and iPads", and does not take "a".
 * Naming each form is duller than deriving it and is the only way the sentences
 * read correctly on both platforms.
 */
interface PlatformCopy {
  /** Column stem, so the two columns are derived from one key and cannot drift. */
  key: PlatformKey
  /** Card title. */
  title: string
  /** After a count word: "every {devices} below 1.2.0". */
  devices: string
  /** Bare plural: "still allowed on {devicesPlural}". */
  devicesPlural: string
  /** With its own article: "anyone on {deviceSingular} below 1.2.0". */
  deviceSingular: string
  /** Where Update Now sends them. */
  store: string
}

const PLATFORMS: PlatformCopy[] = [
  {
    key: 'ios',
    title: 'iPhone & iPad',
    devices: 'iPhone and iPad',
    devicesPlural: 'iPhones and iPads',
    deviceSingular: 'an iPhone or iPad',
    store: 'the App Store',
  },
  {
    key: 'android',
    title: 'Android',
    devices: 'Android device',
    devicesPlural: 'Android devices',
    deviceSingular: 'an Android device',
    store: 'Google Play',
  },
]

/**
 * `1.2.0` or blank. Anything else is dropped by the mobile client's
 * compareVersions (it returns null, which the gate reads as "no floor"), so a
 * malformed floor would quietly do nothing at all. Identical to the validator in
 * components/UrgentNoticeCard.tsx, which guards the same class of mistake on the
 * announcement audience.
 */
function versionLooksWrong(v: string): boolean {
  const t = v.trim()
  return t.length > 0 && !/^\d+(\.\d+)*$/.test(t)
}

function versionIsUsable(v: string | null | undefined): boolean {
  const t = (v ?? '').trim()
  return t.length > 0 && /^\d+(\.\d+)*$/.test(t)
}

export function ForceUpdatePage() {
  const { message: toast, modal } = App.useApp()

  const [config, setConfig] = useState<ForceUpdateConfigRow | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const loadConfig = useCallback(async () => {
    // `select('*')` so a column added later needs no change here — the same
    // reason KillSwitch.tsx reads it that way.
    const { data, error } = await (supabaseClient.from(APP_CONFIG_RESOURCE) as any)
      .select('*')
      .eq('id', true)
      .maybeSingle()
    if (!error) setConfig((data as ForceUpdateConfigRow) ?? null)
    setIsLoading(false)
  }, [])

  useEffect(() => {
    void loadConfig()
  }, [loadConfig])

  /** Persist only the named columns to the single app_config row (id = true). */
  async function patchConfig(values: Partial<ForceUpdateConfigRow>): Promise<void> {
    const { error } = await (supabaseClient.from(APP_CONFIG_RESOURCE) as any)
      .update({ ...values, updated_at: new Date().toISOString() })
      .eq('id', true)
    if (error) throw error
  }

  const iosArmed = !!config?.force_update_ios_active
  const androidArmed = !!config?.force_update_android_active
  const anyArmed = iosArmed || androidArmed

  const stats: Stat[] = PLATFORMS.map((p) => {
    const armed = p.key === 'ios' ? iosArmed : androidArmed
    const floor =
      (p.key === 'ios'
        ? config?.force_update_ios_min_version
        : config?.force_update_android_min_version) ?? ''
    return {
      label: p.title,
      value: isLoading ? '—' : armed && floor ? `Below ${floor} blocked` : 'Off',
      icon: Download,
      tone: armed ? 'gold' : 'neutral',
    }
  })

  const StatusIcon = anyArmed ? Download : ShieldCheck

  return (
    <ListPage
      intro={{
        eyebrow: 'System · App Controls',
        title: 'Force app update',
        description:
          'Sets the OLDEST app version still allowed to run, separately for iPhone and Android. Anyone below their platform’s version sees only an “Update Required” screen with a button to the store — including the sign-in screen, so it reaches members who are not signed in. It takes effect on every connected device within seconds, and turning it off releases them just as fast. Only ever set a version that is ALREADY live in that store: until a member can actually install it, there is no way out of that screen.',
      }}
      stats={stats}
      statColumns={2}
    >
      {/* Status band. Gold when a floor is armed, neutral otherwise — never red;
          red belongs to the kill switch alone (see pages/KillSwitch.tsx). */}
      <div
        data-testid="force-update-status"
        style={{
          background: anyArmed ? 'rgba(196,151,49,.08)' : '#ffffff',
          border: `1.5px solid ${anyArmed ? 'rgba(196,151,49,.35)' : brand.cardLine}`,
          borderRadius: 16,
          padding: '24px 26px',
          marginBottom: 18,
          display: 'flex',
          alignItems: 'center',
          gap: 18,
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 14,
            background: anyArmed ? 'rgba(196,151,49,.12)' : brand.paper,
            border: `1px solid ${anyArmed ? 'rgba(196,151,49,.35)' : brand.cardLine}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <StatusIcon size={30} color={anyArmed ? brand.gold : brand.sub} />
        </div>
        <div>
          <div
            style={{
              fontFamily: brand.fDisplay,
              fontWeight: 800,
              fontSize: 24,
              color: anyArmed ? brand.gold : brand.ink,
              lineHeight: 1.1,
              letterSpacing: '.01em',
            }}
          >
            {isLoading
              ? 'Loading…'
              : anyArmed
                ? 'AN UPDATE IS BEING FORCED'
                : 'NO FORCED UPDATE'}
          </div>
          <div
            style={{
              fontFamily: brand.fSans,
              fontWeight: 500,
              fontSize: 12.5,
              color: brand.sub,
              marginTop: 5,
              maxWidth: 560,
              lineHeight: 1.5,
            }}
          >
            {isLoading
              ? 'Reading the current configuration…'
              : anyArmed
                ? describeArmed(config)
                : 'Every member can use the app on whatever version they have installed.'}
          </div>
        </div>
      </div>

      {PLATFORMS.map((p) => (
        <PlatformPanel
          key={p.key}
          platform={p}
          config={config}
          isLoading={isLoading}
          patchConfig={patchConfig}
          refetch={loadConfig}
          toast={toast}
          modal={modal}
        />
      ))}

      <MessageCard
        config={config}
        isLoading={isLoading}
        patchConfig={patchConfig}
        refetch={loadConfig}
        toast={toast}
      />
    </ListPage>
  )
}

/** One sentence naming exactly who is blocked right now. */
function describeArmed(config: ForceUpdateConfigRow | null): string {
  const parts: string[] = []
  if (config?.force_update_ios_active) {
    parts.push(`iPhone and iPad below ${config.force_update_ios_min_version || '—'}`)
  }
  if (config?.force_update_android_active) {
    parts.push(`Android below ${config.force_update_android_min_version || '—'}`)
  }
  return `Locked out right now: ${parts.join(', ')}. They can do nothing in the app until they update.`
}

interface PanelProps {
  platform: PlatformCopy
  config: ForceUpdateConfigRow | null
  isLoading: boolean
  patchConfig: (values: Partial<ForceUpdateConfigRow>) => Promise<void>
  refetch: () => Promise<void> | void
  toast: ReturnType<typeof App.useApp>['message']
  modal: ReturnType<typeof App.useApp>['modal']
}

/**
 * One platform's floor. Both platforms render through this single component on
 * purpose: two hand-written copies of a control this consequential would drift,
 * and the drift would be invisible until the day one of them was used.
 */
function PlatformPanel({
  platform,
  config,
  isLoading,
  patchConfig,
  refetch,
  toast,
  modal,
}: PanelProps) {
  const activeCol =
    platform.key === 'ios' ? 'force_update_ios_active' : 'force_update_android_active'
  const versionCol =
    platform.key === 'ios'
      ? 'force_update_ios_min_version'
      : 'force_update_android_min_version'

  const savedVersion = (config?.[versionCol] as string | null | undefined) ?? ''
  const armed = !!config?.[activeCol]

  const [draft, setDraft] = useState('')
  const [savingVersion, setSavingVersion] = useState(false)
  const [toggling, setToggling] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Re-seed the draft whenever the server value changes, so the input shows what
  // is actually live rather than a stale keystroke.
  useEffect(() => {
    setDraft(savedVersion)
  }, [savedVersion])

  const warn = versionLooksWrong(draft)
  const dirty = draft.trim() !== savedVersion.trim()
  const canArm = versionIsUsable(savedVersion)

  async function applyVersion() {
    setSavingVersion(true)
    setError(null)
    try {
      await patchConfig({ [versionCol]: draft.trim() } as Partial<ForceUpdateConfigRow>)
      await refetch()
      toast.success(
        draft.trim()
          ? `${platform.title} minimum version saved as ${draft.trim()}.`
          : `${platform.title} minimum version cleared.`,
      )
    } catch (e) {
      const msg = describeWriteError(e)
      setError(msg)
      toast.error(`Failed to save the ${platform.title} version: ${msg}`)
    } finally {
      setSavingVersion(false)
    }
  }

  /**
   * Editing the floor WHILE the switch is on is every bit as consequential as
   * arming it — it takes effect on live devices immediately and can lock out
   * members who were fine a second ago. So it gets the same gate. Saving a floor
   * while the switch is OFF changes nothing for anybody and does not confirm.
   */
  function requestSaveVersion() {
    if (!armed) {
      void applyVersion()
      return
    }
    const next = draft.trim()
    modal.confirm({
      title: next
        ? `Change the ${platform.title} floor from ${savedVersion} to ${next}?`
        : `Clear the ${platform.title} floor while the switch is on?`,
      okText: next ? 'Yes, change it' : 'Yes, clear it',
      okType: 'primary',
      cancelText: 'Cancel',
      width: 560,
      content: (
        <div style={{ fontFamily: brand.fSans, fontSize: 13, lineHeight: 1.6, color: brand.ink }}>
          {next ? (
            <>
              <p style={{ marginTop: 4 }}>
                This is live right now, so the change reaches every {platform.devices} within
                seconds.
              </p>
              <p style={{ color: brand.gold, fontWeight: 600, marginBottom: 0 }}>
                {next} must ALREADY be live in {platform.store}, or everyone below it is locked
                out of the app with no way to fix it.
              </p>
            </>
          ) : (
            <p style={{ marginTop: 4, marginBottom: 0 }}>
              With no floor set, the switch stops blocking anyone — but it stays ON, so setting a
              version later takes effect immediately. Turn the switch off instead if you mean to
              stop forcing the update.
            </p>
          )}
        </div>
      ),
      onOk: () => applyVersion(),
    })
  }

  async function applyToggle(next: boolean) {
    setToggling(true)
    setError(null)
    try {
      await patchConfig({ [activeCol]: next } as Partial<ForceUpdateConfigRow>)
      await refetch()
      if (next) {
        toast.success(
          `Every ${platform.devices} below ${savedVersion} must now update. They see the update screen within seconds.`,
        )
      } else {
        toast.success(`${platform.title} members have full access again.`)
      }
    } catch (e) {
      const msg = describeWriteError(e)
      setError(msg)
      toast.error(`Failed to update the ${platform.title} switch: ${msg}`)
    } finally {
      setToggling(false)
    }
  }

  /**
   * Only the ON direction confirms. Turning a floor off simply restores access
   * and is invisible to anyone already running a current build; turning it on
   * locks every older device out of the whole app, including sign-in. Still
   * `primary`, never `danger` — red is the kill switch's alone.
   */
  function requestToggle(next: boolean) {
    if (!next) {
      void applyToggle(false)
      return
    }
    modal.confirm({
      title: `Force every ${platform.devices} below ${savedVersion} to update?`,
      okText: 'Yes, require the update',
      okType: 'primary',
      cancelText: 'Cancel',
      width: 560,
      content: (
        <div style={{ fontFamily: brand.fSans, fontSize: 13, lineHeight: 1.6, color: brand.ink }}>
          <p style={{ marginTop: 4 }}>
            Every {platform.devices} on a version <strong>below {savedVersion}</strong> will see
            only an “Update Required” screen — <strong>including the sign-in screen</strong>.
            There is no way past it except installing the update.
          </p>
          <p style={{ color: brand.gold, fontWeight: 600 }}>
            So {savedVersion} must ALREADY be live in {platform.store}. If it is not, those
            members are locked out of the app entirely.
          </p>
          <p style={{ marginBottom: 0 }}>
            The other platform is unaffected. Turning this back off releases everyone within
            seconds.
          </p>
        </div>
      ),
      onOk: () => applyToggle(true),
    })
  }

  return (
    <SectionCard
      title={platform.title}
      sub={`The oldest version still allowed on ${platform.devicesPlural}. Update Now sends them to ${platform.store}.`}
      style={{ maxWidth: 720, marginBottom: 18 }}
    >
      <div data-testid={`force-update-${platform.key}-card`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span
            style={{
              fontFamily: brand.fSans,
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: '.05em',
              textTransform: 'uppercase',
              color: brand.sub,
            }}
          >
            Minimum version
          </span>
          <Input
            data-testid={`force-update-${platform.key}-version`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="1.2.0"
            disabled={isLoading || savingVersion}
            style={{ width: 130, fontFamily: brand.fSans }}
          />
          <Button
            data-testid={`force-update-${platform.key}-version-save`}
            onClick={() => requestSaveVersion()}
            loading={savingVersion}
            disabled={isLoading || !dirty || warn}
          >
            Save
          </Button>
        </div>

        {warn && (
          <div data-testid={`force-update-${platform.key}-version-warning`} style={warnStyle}>
            A version must look like <strong>1.4.2</strong>. Anything else is ignored by the app,
            so that floor would quietly do nothing.
          </div>
        )}

        <div style={{ ...rowStyle, marginTop: 16 }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div
              style={{
                fontFamily: brand.fSans,
                fontWeight: 700,
                fontSize: 13,
                color: brand.ink,
              }}
            >
              Require the update
            </div>
            <div data-testid={`force-update-${platform.key}-summary`} style={summaryStyle}>
              {canArm
                ? armed
                  ? `Anyone on ${platform.deviceSingular} below ${savedVersion} is blocked right now. ${savedVersion} and newer keep full access.`
                  : `Turning this on will block anyone on ${platform.deviceSingular} below ${savedVersion}. ${savedVersion} and newer keep full access.`
                : 'Save a minimum version first — there is nothing to enforce until then.'}
            </div>
          </div>
          <Switch
            data-testid={`force-update-${platform.key}-toggle`}
            checked={armed}
            loading={toggling}
            disabled={isLoading || (!armed && !canArm)}
            onChange={(checked) => requestToggle(checked)}
          />
        </div>

        {error && (
          <div data-testid={`force-update-${platform.key}-error`} style={errorStyle}>
            Couldn&rsquo;t save this change: {error}. The switch still shows the setting that is
            actually live — try again.
          </div>
        )}
      </div>
    </SectionCard>
  )
}

interface MessageCardProps {
  config: ForceUpdateConfigRow | null
  isLoading: boolean
  patchConfig: (values: Partial<ForceUpdateConfigRow>) => Promise<void>
  refetch: () => Promise<void> | void
  toast: ReturnType<typeof App.useApp>['message']
}

function MessageCard({ config, isLoading, patchConfig, refetch, toast }: MessageCardProps) {
  const saved = config?.force_update_message ?? ''
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setDraft(saved)
  }, [saved])

  const dirty = draft.trim() !== saved.trim()

  async function save() {
    setSaving(true)
    setError(null)
    try {
      await patchConfig({ force_update_message: draft.trim() })
      await refetch()
      toast.success(
        draft.trim() ? 'Update screen message saved.' : 'Message cleared — the app’s own wording is used.',
      )
    } catch (e) {
      const msg = describeWriteError(e)
      setError(msg)
      toast.error(`Failed to save the message: ${msg}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <SectionCard
      title="Message on the update screen"
      sub="Optional. Explains WHY the update is needed. Shown on both platforms."
      style={{ maxWidth: 720, marginBottom: 18 }}
    >
      <Input.TextArea
        data-testid="force-update-message"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={3}
        disabled={isLoading || saving}
        placeholder="Sign-in has moved to a new system. Please update to continue."
        style={{ fontFamily: brand.fSans }}
      />
      <div style={summaryStyle}>
        Leave it blank and the app uses its own wording (“A new version of the MUAA Alumni app is
        available…”). Anyone can read this text, signed in or not — keep it to what a member needs
        to know.
      </div>
      <div style={{ marginTop: 12 }}>
        <Button
          data-testid="force-update-message-save"
          onClick={() => void save()}
          loading={saving}
          disabled={isLoading || !dirty}
        >
          Save message
        </Button>
      </div>
      {error && (
        <div data-testid="force-update-message-error" style={errorStyle}>
          Couldn&rsquo;t save the message: {error}. Try again.
        </div>
      )}
    </SectionCard>
  )
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 20,
  flexWrap: 'wrap',
}

const summaryStyle: React.CSSProperties = {
  fontFamily: brand.fSans,
  fontSize: 12.5,
  color: brand.sub,
  lineHeight: 1.55,
  marginTop: 8,
}

const warnStyle: React.CSSProperties = {
  fontFamily: brand.fSans,
  fontSize: 12,
  fontWeight: 600,
  color: '#8a6a1f',
  lineHeight: 1.5,
  marginTop: 10,
}

const errorStyle: React.CSSProperties = {
  fontFamily: brand.fSans,
  fontWeight: 600,
  fontSize: 12,
  color: '#a4452f',
  lineHeight: 1.5,
  marginTop: 10,
}
